/**
 * PoHeader (S3.2) — PO detail header card on the trio pattern (SoHeader
 * precedent): back + breadcrumb (採購 › 採購訂單 › PO #), poNumber title +
 * status Tag, source-SO chip (the SO→PO ref), >NT$200k approval banner with
 * ROLE-GATED release/reject (hidden — not disabled — for non-managers),
 * meta row.
 *
 * Actions are affordances, not status noise (hide-don't-disable):
 * - 送出 Send renders only on a draft whose approval gate is cleared.
 * - 記錄供應商確認 renders only while a checkpoint can be captured
 *   (sent / vendor_confirmed / shipped).
 */

import { Alert, Breadcrumb, Button, Card, Divider, Tag, theme } from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TimestampPair } from '@/components/common/TimestampDisplay';
import PurpleTag from '@/components/common/PurpleTag';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import type { PurchaseOrder, PurchaseOrderStatus } from '@/types/api/purchaseOrder';
import { formatMoney } from '../../salesOrders/components/soFormat';

const PO_STATUS_TAG_COLOR: Record<PurchaseOrderStatus, string> = {
  draft: 'default',
  sent: 'blue',
  vendor_confirmed: 'geekblue',
  shipped: 'cyan',
  received: 'green',
};

interface PoHeaderProps {
  po: PurchaseOrder;
  /** Manager-gate result — release/reject render only when true. */
  canDecide: boolean;
  sending: boolean;
  /** Status gate resolved by the page (sent/vendor_confirmed/shipped). */
  showConfirmCheckpoint: boolean;
  onSend: () => void;
  onRelease: () => void;
  onReject: () => void;
  onOpenConfirm: () => void;
}

export const PoHeader = ({
  po,
  canDecide,
  sending,
  showConfirmCheckpoint,
  onSend,
  onRelease,
  onReject,
  onOpenConfirm,
}: PoHeaderProps) => {
  const { t } = useTranslation(['procurement', 'common']);
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const elevation = useElevation();

  const canSend =
    po.status === 'draft' && (!po.approvalRequired || po.approvalStatus === 'approved');

  const metaItem = (label: string, value: React.ReactNode) => (
    <div className="tw-flex tw-items-center tw-gap-2">
      <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>{label}</span>
      <span style={{ fontSize: token.fontSize, fontWeight: token.fontWeightStrong }}>{value}</span>
    </div>
  );

  const actionButtonStyle = {
    height: `${controlHeightTokens.controlHeightXL}px`,
    borderRadius: radiusTokens.card,
  };

  return (
    <Card
      className="tw-mb-6"
      style={{ ...elevation.card }}
    >
      {/* Back + breadcrumb */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/auth/purchase-orders')}
          style={{ padding: 0, height: 'auto', lineHeight: 1 }}
        >
          {t('procurement:detail.backToList')}
        </Button>
        <Breadcrumb
          items={[
            { title: t('common:menu.procurement') },
            {
              title: (
                <a onClick={() => navigate('/auth/purchase-orders')}>
                  {t('procurement:poListTitle')}
                </a>
              ),
            },
            { title: po.poNumber },
          ]}
        />
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Title + status Tag + actions */}
      <div className="tw-flex tw-justify-between tw-items-start tw-gap-6 tw-mb-4 tw-flex-wrap">
        <div className="tw-flex-1" style={{ minWidth: 0 }}>
          <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
            <h1
              style={{
                fontSize: token.fontSizeHeading3,
                fontWeight: token.fontWeightStrong,
                color: token.colorPrimary,
                margin: 0,
                lineHeight: 1.2,
              }}
            >
              {po.poNumber}
            </h1>
            <Tag color={PO_STATUS_TAG_COLOR[po.status]} style={{ margin: 0 }}>
              {t(`procurement:status.${po.status}`)}
            </Tag>
            {po.approvalStatus === 'pending' && (
              <Tag color="orange" style={{ margin: 0 }}>
                {t('procurement:approval.pending')}
              </Tag>
            )}
          </div>
          {po.sourceSoNumber && (
            <p
              style={{
                fontSize: token.fontSizeSM,
                color: token.colorTextTertiary,
                margin: '4px 0 0 0',
              }}
            >
              {t('procurement:fields.sourceSo')}:{' '}
              {po.sourceSoId ? (
                <a
                  onClick={() => navigate(`/auth/sales-orders/${po.sourceSoId}`)}
                  style={{ color: token.colorPrimary, cursor: 'pointer' }}
                >
                  {po.sourceSoNumber}
                </a>
              ) : (
                po.sourceSoNumber
              )}
            </p>
          )}
        </div>

        <div className="tw-flex tw-gap-3 tw-flex-wrap">
          {showConfirmCheckpoint && (
            <Button icon={<CheckOutlined />} onClick={onOpenConfirm} style={actionButtonStyle}>
              {t('procurement:actions.confirmCheckpoint')}
            </Button>
          )}
          {canSend && (
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={sending}
              onClick={onSend}
              style={actionButtonStyle}
            >
              {t('procurement:actions.send')}
            </Button>
          )}
        </div>
      </div>

      {/* >NT$200k approval banner w/ role-gated release/reject */}
      {po.approvalStatus === 'pending' && (
        <Alert
          className="tw-mb-4"
          type="warning"
          showIcon
          message={t('procurement:detail.banner.pendingTitle')}
          description={
            canDecide
              ? t('procurement:detail.banner.pendingDescription')
              : `${t('procurement:detail.banner.pendingDescription')} ${t(
                  'procurement:detail.banner.managerOnly'
                )}`
          }
          action={
            canDecide ? (
              <div className="tw-flex tw-gap-2">
                <Button icon={<CheckOutlined />} onClick={onRelease} style={actionButtonStyle}>
                  {t('procurement:detail.banner.releaseAction')}
                </Button>
                <Button
                  danger
                  icon={<CloseOutlined />}
                  onClick={onReject}
                  style={actionButtonStyle}
                >
                  {t('procurement:detail.banner.rejectAction')}
                </Button>
              </div>
            ) : undefined
          }
        />
      )}
      {po.approvalStatus === 'rejected' && (
        <Alert
          className="tw-mb-4"
          type="error"
          showIcon
          message={t('procurement:detail.banner.rejectedTitle')}
          description={t('procurement:detail.banner.rejectedDescription')}
        />
      )}

      <Divider style={{ margin: '16px 0' }} />

      {/* Meta row */}
      <div className="tw-flex tw-gap-8 tw-flex-wrap tw-items-center" style={{ paddingTop: '8px' }}>
        {metaItem(
          t('procurement:fields.vendor'),
          <span className="tw-inline-flex tw-items-center tw-gap-2">
            <a
              onClick={() => navigate(`/auth/vendors/${po.vendorId}`)}
              style={{ color: token.colorPrimary, cursor: 'pointer' }}
            >
              {po.vendorName}
            </a>
            {po.vendorPrefillSource === 'preferred' && (
              <PurpleTag style={{ margin: 0 }}>
                {t('procurement:fields.preferredVendor')}
              </PurpleTag>
            )}
          </span>
        )}
        {metaItem(t('procurement:fields.orderDate'), po.orderDate || '-')}
        {metaItem(t('procurement:fields.expectedDelivery'), po.expectedDeliveryDate || '-')}
        {metaItem(t('procurement:fields.paymentTerms'), po.paymentTerms || '-')}
        {metaItem(t('procurement:fields.total'), formatMoney(po.currency, po.totalAmount))}
        <TimestampPair createdAt={po.createdAt} updatedAt={po.updatedAt} direction="horizontal" />
      </div>
    </Card>
  );
};

export default PoHeader;
